import Bugsnag from "@bugsnag/js";
import { Capacitor } from "@capacitor/core";
import { api } from "../api/client";
import { dbTokenStore } from "../api/tokens";
import { queryClient } from "../query/client";
import { keys } from "../query/keys";
import { errorLog } from "../utils/logger";

/**
 * Ask for permission to send push notifications (the apps only), and register the device if it's
 * given. Resolves to whether it was. Signing in and the account step ask, as before.
 */
export async function registerPushPermission(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) {
    return false;
  }
  const { PushNotifications } = await import("@capacitor/push-notifications");
  let { receive } = await PushNotifications.checkPermissions();
  if (receive === "prompt") {
    ({ receive } = await PushNotifications.requestPermissions());
  }
  if (receive === "granted") {
    await PushNotifications.register();
  }
  return receive === "granted";
}

/**
 * Register the device again when a saved session opens, without asking. Android drops a token that
 * changes while the app isn't running, and Apple expects the app to register at each launch. It only
 * registers when permission was already given, so nobody is asked as the app starts, and it never
 * fails: a device that can't be registered is no reason for the app not to start.
 */
export async function refreshPushRegistration(): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    return;
  }
  try {
    if (!(await dbTokenStore.get())) {
      return;
    }
    const { PushNotifications } = await import("@capacitor/push-notifications");
    const { receive } = await PushNotifications.checkPermissions();
    if (receive === "granted") {
      await PushNotifications.register();
    }
  } catch (err) {
    report(err);
  }
}

/**
 * Give the API the device's token each time the device registers for pushes: registering is what
 * sends it, and a device can send a new one at any time. Returns what stops listening.
 */
export async function listenForPushRegistration(): Promise<() => Promise<void>> {
  const { PushNotifications } = await import("@capacitor/push-notifications");
  const handles = [
    await PushNotifications.addListener("registration", ({ value }) => void addDevice(value)),
    await PushNotifications.addListener("registrationError", ({ error }) =>
      report(new Error(error)),
    ),
  ];
  return async () => {
    await Promise.all(handles.map((handle) => handle.remove()));
  };
}

/** Add the device to the account's, unless it's there already. */
async function addDevice(token: string): Promise<void> {
  try {
    // A token can come in as the session ends, after its tokens are gone: it's sent again at the
    // next sign-in, which registers again.
    if (!(await dbTokenStore.get())) {
      return;
    }
    const { Device } = await import("@capacitor/device");
    const [device, { data: devices }] = await Promise.all([
      Device.getInfo(),
      api.railsLegacyV2.getDevices(),
    ]);
    // The token's case isn't always the same.
    const known = devices.some(({ device_id }) => device_id.toLowerCase() === token.toLowerCase());
    if (!known) {
      await api.railsLegacyV2.createDevice({
        device_id: token,
        // iOS 16 and later give a generic name: Apple turned down Docket's request for the one the
        // user gave the device.
        name:
          device.name || (Capacitor.getPlatform() === "ios" ? "iPhone Device" : "Android Device"),
        platform: device.platform,
      });
    }
    await queryClient.invalidateQueries({ queryKey: keys.devices });
  } catch (err) {
    report(err);
  }
}

function report(err: unknown): void {
  errorLog(err, "Could not register the device for push notifications");
  Bugsnag.notify(err instanceof Error ? err : new Error(String(err)));
}
